import { Link } from 'react-router-dom';
import { CATEGORY_LIST, PRODUCTS } from '../../data/catalog';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import { ArrowRight } from '../ui/Icons';


/**
 * Editorial grid of the seven product categories. The first tile takes a
 * 2x2 slot on desktop; the rest fill in around it. Everything stacks on mobile.
 */
const ShopByCategory = () => (
  <section className="vp-section bg-white">
    <div className="container">
      <SectionHeading
        eyebrow="Shop by category"
        title="Every surface in the house, covered"
        lead={`${PRODUCTS.filter((p) => !p.hidden).length} products across ${CATEGORY_LIST.length} ranges — from primers and putties to exterior emulsions, wood finishes and waterproofing.`}
        className="mb-10"
        action={
          <Link to="/products" className="vp-btn vp-btn-outline">
            Browse all products
            <ArrowRight size={16} />
          </Link>
        }
      />

      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {CATEGORY_LIST.map((c, i) => {
          const large = i === 0;
          // 1 large (2x2) + 6 tiles leaves one cell over on a 3-col grid — the last tile runs full width.
          const wide = i === CATEGORY_LIST.length - 1 && (CATEGORY_LIST.length - 1) % 3 === 0;
          return (
            <Reveal
              as="li"
              key={c.key}
              delay={Math.min(i, 4) as 0 | 1 | 2 | 3 | 4}
              className={`${large ? 'sm:col-span-2 lg:row-span-2' : ''} ${wide ? 'lg:col-span-3' : ''}`}
            >
              <Link
                to={c.route}
                className={`group relative block w-full overflow-hidden rounded-2xl bg-brand-navy ${
                  large ? 'h-72 sm:h-80 lg:h-full lg:min-h-[31rem]' : 'h-56 lg:h-60'
                }`}
              >
                <img
                  src={c.heroImage}
                  alt={`${c.name} by Vertex Paints`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-premium group-hover:scale-105"
                />
                {/* Navy scrim */}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/95 via-brand-navy/40 to-brand-navy/10" />
                <div className="absolute inset-0 bg-brand-navy/0 group-hover:bg-brand-navy/20 transition-colors duration-500" />

                <div className="relative h-full flex flex-col justify-end p-5 sm:p-6 lg:p-7">
                  <span className="vp-eyebrow vp-eyebrow-light">{c.eyebrow}</span>
                  <div className="mt-3 flex items-end justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className={`font-bold text-white leading-tight ${large ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl'}`}>{c.name}</h3>
                      <p className="mt-1.5 text-sm text-white/70">
                        {c.products.length} {c.products.length === 1 ? 'product' : 'products'}
                        {large && <span className="hidden sm:inline"> · {c.tagline}</span>}
                      </p>
                    </div>
                    <span className="flex-shrink-0 w-11 h-11 rounded-full bg-white/15 border border-white/25 backdrop-blur-sm text-white flex items-center justify-center transition-all duration-300 ease-premium group-hover:bg-brand-gold group-hover:border-brand-gold group-hover:text-brand-ink">
                      <ArrowRight size={18} />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </ul>
    </div>
  </section>
);

export default ShopByCategory;
